import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import { get_app_logo, s3baseUrl } from "src/config/config";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { gr } from "src/design-system/tokens";
import GrSelect from "src/design-system/components/GrSelect";
import { GroupMenuButton } from "./GroupsFeeds";
import GrAvatar from "src/design-system/components/GrAvatar";

// The 32px avatar inside a 44px field: the input's own padding shrinks to the space left.
const selectSx = {
  "&& .MuiInputBase-input:not(textarea)": {
    display: "flex",
    alignItems: "center",
    py: `calc((${gr.control.touch} - ${gr.control.sm}) / 2)`,
  },
};
const menuProps = {
  sx: { "& .MuiMenu-paper": { maxHeight: `calc(${gr.space[12]} * 6)` } },
};
const optionSx = {
  display: "flex",
  alignItems: "center",
  gap: gr.space[2],
  minWidth: 0,
};
const titleSx = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// One look for an option, in the list and in the closed field.
function GroupOption({ option }) {
  return (
    <Box component="span" sx={optionSx}>
      <GrAvatar
        size="sm"
        src={option.src}
        name={option.title}
        sx={{ borderRadius: gr.radius.chip }}
      />
      <Box component="span" sx={titleSx}>
        {option.title}
      </Box>
    </Box>
  );
}

GroupOption.propTypes = {
  option: PropTypes.shape({ src: PropTypes.string, title: PropTypes.string })
    .isRequired,
};

// Group picker above the feed on phones and tablets (the groups card takes this role on large screens).
export default function MobilesTabs({
  data,
  onGroupDetail,
  onLeaveGroup,
  groupDetailLoading,
}) {
  const { selectedGroup, setSelectedGroup } = useContentSetting();

  const handleGroupFeed = (value) => {
    localStorage.setItem("selectedGroup", value || "general");
    setSelectedGroup(value || "");
  };

  const options = [
    { value: "", title: "General Community", src: get_app_logo() },
    ...(data || []).map((item) => ({
      value: item._id,
      title: item.title,
      src: s3baseUrl + item?.image?.thumbnail_1,
    })),
  ];
  const optionFor = (value) =>
    options.find((o) => o.value === value) || options[0];

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: gr.space[2] }}>
      <GrSelect
        fullWidth
        value={selectedGroup || ""}
        onChange={(e) => handleGroupFeed(e.target.value)}
        inputProps={{ "aria-label": "Groups" }}
        sx={selectSx}
        SelectProps={{
          displayEmpty: true,
          renderValue: (selected) => (
            <GroupOption option={optionFor(selected)} />
          ),
          MenuProps: menuProps,
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value || "general"} value={option.value}>
            <GroupOption option={option} />
          </MenuItem>
        ))}
      </GrSelect>
      {selectedGroup && onGroupDetail ? (
        <GroupMenuButton
          onGroupDetail={() => onGroupDetail(selectedGroup)}
          onLeaveGroup={() =>
            onLeaveGroup(
              (data || []).find((item) => item._id === selectedGroup)
            )
          }
          disabled={groupDetailLoading}
        />
      ) : null}
    </Box>
  );
}

MobilesTabs.propTypes = {
  data: PropTypes.array,
  onGroupDetail: PropTypes.func,
  onLeaveGroup: PropTypes.func,
  groupDetailLoading: PropTypes.bool,
};
